import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  type Cadence,
  type SchedulableAuditType,
  SCHEDULABLE_AUDIT_TYPES,
  describeCadence,
  resolveCadence,
} from '@traaker/shared';
import { api } from '../../services/api';

const AXIS_LABELS: Record<SchedulableAuditType, string> = {
  website: 'Audit Website',
  earned_media: 'Earned Media',
  brand_narrative: 'Brand Narrative',
  brand_authority: 'Brand Authority',
  brand_referral: 'Brand Referral',
  news_watch: 'Veille news',
};

interface Props {
  brandId: string;
  tier: string;
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined;
  onSaved: () => Promise<void> | void;
}

/**
 * Per-axis cadence editor for one brand.
 *
 * Layout: one row per axis with a toggle (on/off), interval input, and unit
 * select. Tier-default values render as placeholder text when no override is
 * set; toggling off explicitly stores `'off'` (which differs from "use tier
 * default" — see plan doc §0.2 D-2).
 *
 * On save, calls `PATCH /brands/:brandId/audit-config` with the diff.
 */
export default function CadenceEditor({ brandId, tier, cadenceOverride, onSaved }: Props) {
  const [draft, setDraft] = useState<
    Partial<Record<SchedulableAuditType, Cadence | undefined>>
  >(cadenceOverride || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(cadenceOverride || {});
    setError(null);
  }, [cadenceOverride, brandId]);

  const setAxis = (axis: SchedulableAuditType, c: Cadence | undefined) => {
    setDraft((prev) => ({ ...prev, [axis]: c }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only send the override map (excluding `undefined` entries — those
      // mean "fall through to tier default" and are stored as omitted keys).
      const cleaned: Partial<Record<SchedulableAuditType, Cadence>> = {};
      for (const axis of SCHEDULABLE_AUDIT_TYPES) {
        const v = draft[axis];
        if (v !== undefined) cleaned[axis] = v;
      }
      await api.patch(`/brands/${brandId}/audit-config`, { cadenceOverride: cleaned });
      await onSaved();
    } catch (err: any) {
      setError(err?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Fréquence des audits par axe. Laisser vide pour appliquer le défaut du tier.
        L&apos;heure exacte de déclenchement varie de ±4h à ±24h selon la cadence.
      </Typography>

      {SCHEDULABLE_AUDIT_TYPES.map((axis) => {
        const value = draft[axis];
        const tierDefault = resolveCadence(undefined, tier, axis);
        const effective = value !== undefined ? value : tierDefault;
        const isOn = effective !== 'off';
        const editable = value !== undefined ? value : tierDefault;
        const intervalValue =
          editable !== 'off' && typeof editable === 'object' ? editable.interval : 1;
        const unitValue =
          editable !== 'off' && typeof editable === 'object' ? editable.unit : 'days';
        const isOverridden = value !== undefined;

        return (
          <Box
            key={axis}
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px 60px 1fr 130px 140px 32px',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {AXIS_LABELS[axis]}
            </Typography>

            <Switch
              size="small"
              checked={isOn}
              onChange={(e) =>
                setAxis(
                  axis,
                  e.target.checked ? { interval: intervalValue, unit: unitValue } : 'off',
                )
              }
            />

            <Typography variant="caption" color="text.secondary">
              {isOn
                ? `${describeCadence(effective)}${isOverridden ? '' : ' (défaut tier)'}`
                : 'Désactivé'}
            </Typography>

            <TextField
              size="small"
              type="number"
              disabled={!isOn}
              value={intervalValue}
              inputProps={{ min: 1, max: 30 }}
              onChange={(e) => {
                const n = Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 1));
                setAxis(axis, { interval: n, unit: unitValue });
              }}
            />

            <FormControl size="small">
              <Select
                disabled={!isOn}
                value={unitValue}
                onChange={(e) =>
                  setAxis(axis, {
                    interval: intervalValue,
                    unit: e.target.value as 'days' | 'weeks' | 'months',
                  })
                }
              >
                <MenuItem value="days">Jour(s)</MenuItem>
                <MenuItem value="weeks">Semaine(s)</MenuItem>
                <MenuItem value="months">Mois</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Restaurer le défaut du tier">
              <span>
                <IconButton
                  size="small"
                  disabled={!isOverridden}
                  onClick={() => setAxis(axis, undefined)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {saving ? 'Enregistrement…' : 'Enregistrer la cadence'}
        </Button>
      </Box>
    </Stack>
  );
}
